import {
  useEffect,
  useState
} from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import {
  Route,
  Routes,
  useNavigate,
  useSearchParams
} from 'react-router-dom'
import {
  ChakraProvider,
  extendTheme
} from '@chakra-ui/react'
import { Capacitor } from '@capacitor/core'
import {
  getNotificationMessage,
  authSelectors,
  getAdminNotifications,
  getFriendsNotifications,
  getMarkers,
  getPinnedFriends,
  getPosts,
  getProfileItem,
  getSearchParams,
  getUser,
  setFilters,
  setGetProfileItemStatus,
  setOpenPopupBlogs,
  showCurrentChatFriendMarkerCoordinate,
  usersSelectors,
  store,
  setIsSignIn,
  setAskForLocationServices,
  getGuestNotification,
  setCloseAllDrawers,
  setEventId,
  setGetPostsStatus,
  setMarkerOpenBeforeLogin,
  setPosts,
  setUserSectionPage,
  getEventMarkers,
  markersSelectors,
  getEventData,
  clearEventMarkers,
  setOpenPopupEvents,
  setEventData,
  getBlockedRequest
} from 'store'
import { chatCredentialsSelectors } from 'store/chat/selectors'
import {
  ConnectToChat,
  getChatCredentials
} from 'store/chat/services'
import {
  ERR_MSG_GEO_LOC,
  LOCATION_TYPE,
  MY_PROFILES,
  PENDING,
  REJECTED,
  SUCCEEDED
} from 'utils/constants'
import {
  chatCounter,
  isAuth
} from 'utils/helpers'

import {
  Content,
  Layout
} from 'components'
import {
  AboutUs,
  ContactUs
} from 'components/ComingSoon'
import { FAQ } from 'components/FAQ'
import { NotFound } from 'components/NotFound'
import { PrivacyPolicy } from 'components/PrivacyPolicy'
import { TermsOfService } from 'components/TermsOfService'

import {
  changeNumUnread,
  setMeXmppProblem,
  setXmppLiveLocation
} from 'store/chat/slice'
import { useGeoLocation } from 'hooks/useGeoLocation'
import loadCordovaScript from 'utils/cordova_helpers'

// eslint-disable-next-line import/no-extraneous-dependencies, global-require
const Favico = require('favico.js-slevomat')

const customTheme = {
  fonts: {
    body: 'Ubuntu, Roboto, sans-serif',
    heading: 'Ubuntu, Roboto, sans-serif',
    mono: 'Ubuntu, Roboto, sans-serif'
  }
}

const theme = extendTheme(customTheme)

if (localStorage.getItem('recentSearches') === null) {
  localStorage.setItem('recentSearches', '[]')
  localStorage.setItem('is-filtered', false)
}
localStorage.setItem('isNotifOpen', 'false')
sessionStorage.setItem('isChatPanelOpen', 'false')
sessionStorage.setItem('isSharingBlog', 'false')
sessionStorage.removeItem('chosenFriends')
localStorage.setItem('is-notif-modal-open', 'false')

if (window.location.href.includes('post-id') || window.location.href.includes('id')) {
  sessionStorage.removeItem('eventid')
}

// IMPORTANT!: this is to get converse object fully loaded.
// For details please see 'node_modules\converse.js\src\entry.js'
if (!window.converseLoadedListener) {
  window.addEventListener('converse-loaded', () => {
    if (typeof converse === 'object') {
      /* eslint-disable no-undef */
      converse.initialize()
      /* eslint-enable no-undef */
    }
  })
  window.converseLoadedListener = true
}

loadCordovaScript()

const searchParamsTemp = new URLSearchParams(window.location.search)
if (window.location.search.includes('?post-id')) {
  sessionStorage.setItem('postId', searchParamsTemp.get('post-id'))
} else {
  sessionStorage.removeItem('postId')
}

if (window.location.search.includes('?eventid')) {
  sessionStorage.setItem('eventid', searchParamsTemp.get('eventid'))
} else {
  sessionStorage.removeItem('eventid')
}

if (!Capacitor.isNativePlatform()) {
  if (window.location.search.includes('?open_chat_of_jid')) {
    sessionStorage.setItem('open_chat_of_jid', searchParamsTemp.get('open_chat_of_jid'))
  } else {
    sessionStorage.removeItem('open_chat_of_jid')
  }
}

export const App = () => {
  const {
    activeProfile,
    profileItemId,
    userProfile,
    getEventDataStatus,
    getPostsStatus,
    getUserProfileStatus,
    markerOpenBeforeLogin,
    user
  } = useSelector(usersSelectors)
  const { signInStatus } = useSelector(authSelectors)
  const {
    eventId,
    eventMarkers
  } = useSelector(markersSelectors)
  const { chatCredentials } = useSelector(chatCredentialsSelectors)
  const [stopMarkerOpen, setStopMarkerOpen] = useState(true)
  const [getLocationServices, setGetLocationServices] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()

  const dispatch = useDispatch()
  const navigate = useNavigate()
  useGeoLocation()
  const textArea = document.getElementById('textarea-chat')

  textArea?.addEventListener('input', () => {
    textArea.scrollTo({ top: textArea.scrollHeight })
  })

  if (!window.converseEventsListeners) {
    const favico = new Favico({
      animation: 'none'
    })
    window.addEventListener('custom-chat-num_unread', (e) => {
      dispatch(changeNumUnread(e.detail.num_unread))
      favico.badge(chatCounter(e.detail.num_unread))
    })

    window.addEventListener('custom-chat-show_on_map', (e) => {
      const { jid } = e.detail
      if (!jid) return

      const currentState = store.getState()
      const friendUserProfileId = Number(jid.split('-')[0])

      // first check my own profile
      const profile = currentState.users.userProfile.find((item) => item.id === friendUserProfileId)
      if (profile === undefined) {
        // if id is not in my profiles, check friends
        const friendMarker = currentState.markers
          .markers.find((marker) => marker.id === friendUserProfileId)

        if (friendMarker === undefined) {
          dispatch(
            getNotificationMessage({
              type: REJECTED,
              message: `Profile id ${friendUserProfileId} not found`,
              isEditProfile: false
            })
          )

          return
        }

        const event = new CustomEvent('close-chat-modal')
        window.dispatchEvent(event)

        dispatch(setEventId(null))
        dispatch(setCloseAllDrawers(true))
        setTimeout(() => dispatch(setCloseAllDrawers(false)), 850)

        setTimeout(() => {
          const isLiveLocation = friendMarker.isLive ?? false
          let coord = {
            coord: friendMarker.coordinates,
            isStatic: !isLiveLocation
          }
          dispatch(showCurrentChatFriendMarkerCoordinate(coord))

          sessionStorage.setItem('openProfileAfterAnimation', friendUserProfileId)
        }, 850)

        return
      }

      const activeLocType = profile?.location_type
      const isLiveLocation = (
        activeLocType?.[LOCATION_TYPE.LiveLocation] === 0
        || activeLocType?.[LOCATION_TYPE.LiveApproximate] === 1
      )

      const event = new CustomEvent('close-chat-modal')
      window.dispatchEvent(event)

      dispatch(setEventId(null))
      dispatch(setCloseAllDrawers(true))
      setTimeout(() => dispatch(setCloseAllDrawers(false)), 850)

      setTimeout(() => {
        let coord = {
          coord: profile.coordinates,
          isStatic: !isLiveLocation
        }
        dispatch(showCurrentChatFriendMarkerCoordinate(coord))

        sessionStorage.setItem('openProfileAfterAnimation', friendUserProfileId)
      }, 850)
    })

    window.addEventListener('custom-chat-xmpp_account_problem', (e) => {
      dispatch(setMeXmppProblem(e.detail))
      dispatch(getNotificationMessage({
        type: REJECTED,
        message: e.detail.message,
        isEditProfile: e.detail.message.includes('password is incorrect') ?? false
      }))
    })

    window.converseEventsListeners = true
  }

  const checkIfGeoLocIsEnabled = () => {
    navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
      if (permissionStatus?.state === 'granted') {
        localStorage.setItem('isLocDisabled', false)

        navigator.geolocation.getCurrentPosition(
          (res) => {
            localStorage.setItem('isLocDisabled', false)

            if (
              !Number.isNaN(parseFloat(res?.coords?.latitude))
              && !Number.isNaN(parseFloat(res?.coords?.longitude))
            ) {
              const intervalId = setInterval(() => {
                if (sessionStorage.getItem('id') === null) {
                  dispatch(setXmppLiveLocation(res.coords))
                  clearInterval(intervalId)
                }
              }, 3000)
            } else {
              dispatch(
                getNotificationMessage({
                  type: REJECTED,
                  message: ERR_MSG_GEO_LOC[1],
                  isEditProfile: true
                })
              )
            }
          },
          (err) => {
            dispatch(
              getNotificationMessage({
                type: REJECTED,
                message: ERR_MSG_GEO_LOC[1],
                isEditProfile: true
              })
            )

            localStorage.setItem('isLocDisabled', true)
          }
        )
      } else {
        localStorage.setItem('isLocDisabled', true)
      }
    })
  }

  const openBlogLinkPost = () => {
    const postId = searchParams.get('post-id') ?? sessionStorage.getItem('postId')

    if (postId && getPostsStatus !== PENDING) {
      dispatch(setUserSectionPage('clear'))

      dispatch(getPosts({
        type: 'single',
        postId,
        ...(activeProfile && { id: activeProfile?.id })
      }))
      dispatch(setOpenPopupBlogs(true))

      sessionStorage.setItem('postId', postId)

      setTimeout(() => setSearchParams({ 'post-id': postId }), 1000)
    }
  }

  const openEventLink = () => {
    const eventIdTemp = searchParams.get('eventid') ?? sessionStorage.getItem('eventid')

    if (eventIdTemp && getEventDataStatus !== PENDING) {
      dispatch(setUserSectionPage('clear'))

      dispatch(getEventData(eventIdTemp))
      dispatch(setOpenPopupEvents(true))

      sessionStorage.setItem('eventid', eventIdTemp)

      setTimeout(() => setSearchParams({ eventid: eventIdTemp }), 1000)
    }
  }

  useEffect(() => {
    if (activeProfile?.id) {
      dispatch(getChatCredentials())
      dispatch(getMarkers({ isAuth: true }))
      dispatch(getEventMarkers({ isAuth: true }))
      dispatch(getAdminNotifications())
      dispatch(getFriendsNotifications())
      dispatch(getBlockedRequest(activeProfile?.id))
      setStopMarkerOpen(false)
      dispatch(getPinnedFriends())

      localStorage.setItem('activeProfileLat', activeProfile?.coordinates[0])
      localStorage.setItem('activeProfileLng', activeProfile?.coordinates[1])

      if (sessionStorage.getItem('postId')) {
        openBlogLinkPost()
        dispatch(setGetPostsStatus(PENDING))
        dispatch(setPosts([]))
      }

      const ssEventId = Number(sessionStorage.getItem('eventid'))
      const ssEventData = eventMarkers.find((e) => e.id === ssEventId)
      if (
        ssEventId
        && !activeProfile?.affinities.some((a) => ssEventData?.affinities?.includes(a.title))
      ) {
        dispatch(setEventData(ssEventData))
        openEventLink()
      }
    } else if (activeProfile === undefined) {
      setStopMarkerOpen(false)
    }
  }, [activeProfile?.id])

  useEffect(() => {
    if (
      getUserProfileStatus === SUCCEEDED
      && activeProfile?.id === undefined
    ) {
      openBlogLinkPost()

      if (sessionStorage.getItem('postId')) {
        dispatch(setGetPostsStatus(PENDING))
        dispatch(setPosts([]))
      }

      const ssEventId = Number(sessionStorage.getItem('eventid'))
      const ssEventData = eventMarkers.find((e) => e.id === ssEventId)
      if (
        ssEventId
        && !activeProfile?.affinities.some((a) => a.title === ssEventData?.affinities)
      ) {
        dispatch(setEventData(ssEventData))
        openEventLink()
      }
    }
  }, [getUserProfileStatus])

  useEffect(() => {
    // This is for accounts without a profile to be able to open a marker
    if (activeProfile?.id || activeProfile === undefined) {
      setStopMarkerOpen(false)
    }
  }, [activeProfile])

  useEffect(() => {
    if (chatCredentials?.username) {
      dispatch(ConnectToChat())
    }
  }, [chatCredentials])

  useEffect(() => {
    if (
      (
        profileItemId
        && !stopMarkerOpen
        && isAuth()
        && eventId === null
      )
      || (
        !isAuth()
        && profileItemId
        && eventId === null
      )
    ) {
      dispatch(getProfileItem(profileItemId))
    } else if (
      (
        profileItemId
        && stopMarkerOpen
        && eventId === null
      )
      || (
        !isAuth()
        && profileItemId
        && eventId === null
      )
    ) {
      dispatch(getProfileItem.pending())
    }
  }, [profileItemId, eventId, stopMarkerOpen])

  useEffect(() => {
    if (userProfile === null) {
      setStopMarkerOpen(true)
    }
  }, [userProfile])

  useEffect(() => {
    if (signInStatus === SUCCEEDED) {
      if (getUserProfileStatus === SUCCEEDED) {
        dispatch(setGetProfileItemStatus(0))
      } else if (getUserProfileStatus !== SUCCEEDED) {
        dispatch(getProfileItem.pending())
      }
    }
  }, [signInStatus, getUserProfileStatus])

  useEffect(() => {
    if (eventId && profileItemId === '') {
      dispatch(getEventData(eventId))
    }
  }, [eventId])

  useEffect(() => {
    if (
      user
      && signInStatus === SUCCEEDED
    ) {
      dispatch(setUserSectionPage('clear'))

      if (
        !sessionStorage.getItem('postId')
        && !sessionStorage.getItem('id')
        && !sessionStorage.getItem('eventid')
        && !sessionStorage.getItem('openProfileMarkerAfterLogin')
      ) {
        setTimeout(() => dispatch(setUserSectionPage(MY_PROFILES)), 850)
      }
    }
  }, [user?.id, signInStatus])

  // This will only run after a successful act of logging in
  useEffect(() => {
    const run = async () => {
      if (signInStatus === SUCCEEDED) {
        localStorage.setItem('recentSearches', '[]')
        dispatch(setFilters([]))
        dispatch(getUser())
        dispatch(getMarkers({ isAuth: true }))
        dispatch(getEventMarkers({ isAuth: true }))
        dispatch(setIsSignIn(true))
        navigate('/')
        dispatch(getSearchParams(''))
        setGetLocationServices(true)
        checkIfGeoLocIsEnabled()

        if (!Capacitor.isNativePlatform()) {
          try {
            await navigator.serviceWorker.register('/service-worker.js')
            await Notification.requestPermission()
          } catch (err) {
            console.error('SW registration failed: ' + err.message)
          }
        }

        if (markerOpenBeforeLogin) {
          dispatch(setUserSectionPage('clear'))
          dispatch(setMarkerOpenBeforeLogin(false))
        }
      }
    }

    run()
  }, [signInStatus])

  // This will only run if the account is already authenticated
  useEffect(() => {
    const run = async () => {
      if (isAuth()) {
        dispatch(getUser())
        dispatch(getMarkers({ isAuth: true }))
        dispatch(getEventMarkers({ isAuth: true }))
        dispatch(getSearchParams(''))
        setGetLocationServices(true)
        checkIfGeoLocIsEnabled()

        if (!Capacitor.isNativePlatform()) {
          try {
            navigator.serviceWorker.register('/service-worker.js')
            await Notification.requestPermission()
          } catch (err) {
            console.error('SW registration failed: ' + err.message)
          }
        }
      } else {
        dispatch(getMarkers({ isAuth: false }))
        dispatch(clearEventMarkers())
        dispatch(getGuestNotification())
        openBlogLinkPost()
        openEventLink()
      }
    }

    run()
  }, [isAuth])

  useEffect(() => {
    if (
      process.env.REACT_APP_NODE_ENV !== 'CORDOVA_APPLICATION'
      && getLocationServices
      && (
        userProfile !== null
        && userProfile.length
      )
    ) {
      const usesLiveLoc = userProfile.some((profile) => {
        const locationType = profile.location_type ? Object.values(profile.location_type)[0] : null
        return locationType === 0 || locationType === 1
      })

      const hasUsedProfile = userProfile.some((profile) => profile.full_name !== null)

      if (usesLiveLoc || !hasUsedProfile) {
        navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
          if (
            permissionStatus?.state === 'granted'
            || permissionStatus?.state === 'prompt'
          ) {
            navigator.geolocation.getCurrentPosition(
              (res) => {
                localStorage.setItem('isLocDisabled', false)

                if (
                  !Number.isNaN(parseFloat(res?.coords?.latitude))
                  && !Number.isNaN(parseFloat(res?.coords?.longitude))
                ) {
                  const intervalId = setInterval(() => {
                    if (sessionStorage.getItem('id') === null) {
                      dispatch(setXmppLiveLocation(res.coords))
                      clearInterval(intervalId)
                    }
                  }, 3000)
                } else {
                  dispatch(
                    getNotificationMessage({
                      type: REJECTED,
                      message: ERR_MSG_GEO_LOC[1],
                      isEditProfile: true
                    })
                  )
                }
              },
              (err) => {
                dispatch(
                  getNotificationMessage({
                    type: REJECTED,
                    message: usesLiveLoc ? ERR_MSG_GEO_LOC[2] : ERR_MSG_GEO_LOC[0],
                    isEditProfile: true
                  })
                )

                localStorage.setItem('isLocDisabled', true)
              }
            )
          } else {
            if (usesLiveLoc) {
              dispatch(
                getNotificationMessage({
                  type: REJECTED,
                  message: 'Kindly allow Location Services as you are using Live Location',
                  isEditProfile: true
                })
              )
            }

            localStorage.setItem('isLocDisabled', true)
          }
        })

        dispatch(setAskForLocationServices(false))
      }

      setGetLocationServices(false)
    }
  }, [userProfile, getLocationServices])

  // For conversejs
  window.converseEnv = {
    reactAppApiUrl: process.env.REACT_APP_API_URL
  }

  return (
    <ChakraProvider theme={theme}>
      <Routes>
        <Route
          path="/contact-us"
          element={(
            <ContactUs />
          )}
        />
        <Route
          path="/about-us"
          element={(
            <AboutUs />
          )}
        />
        <Route
          path="/terms-of-service"
          element={(
            <TermsOfService />
          )}
        />
        <Route
          path="/FAQ"
          element={(
            <FAQ />
          )}
        />
        <Route
          path="/privacy-policy"
          element={(
            <PrivacyPolicy />
          )}
        />
        <Route path="/404" element={<NotFound />} />
        <Route
          path="*"
          element={(
            <Layout>
              <Content />
            </Layout>
          )}
        />
      </Routes>
    </ChakraProvider>
  )
}
